import React, { useEffect, useState } from 'react';
import {
  Cpu,
  Award,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RotateCw,
  Binary
} from 'lucide-react';
import apiService from '../services/apiService';

const PerformanceIndicators: React.FC = () => {
  const [performance, setPerformance] = useState<any>(null);
  const [weights, setWeights] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const perfData = await apiService.getForecastingPerformance();
        setPerformance(perfData);
      } catch (err: any) {
        if (err.message && err.message.includes('404')) {
          setError('No benchmark performance metrics found. Please train forecasting models first.');
        } else {
          setError(err.message || 'Failed to load model accuracy indicators');
        }
        return;
      }

      try {
        const weightsData = await apiService.getForecastingWeights();
        setWeights(weightsData);
      } catch (err) {
        console.warn('LSTM layer weights not available:', err);
        setWeights(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-12">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
        <p className="text-xs font-semibold text-muted-foreground">Evaluating neural loss benchmarks & weights...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="glass-panel p-6 rounded-2xl border border-border shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-700 text-white flex items-center justify-center shadow-md shadow-purple-500/20">
            <Cpu className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-foreground">Model Performance & Neural Weights</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                Evaluation Suite
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Comparative error metrics (RMSE, MAPE, R²) and layer weight inspectability
            </p>
          </div>
        </div>

        {performance && (
          <div className="flex items-center gap-2 px-3.5 py-2 bg-emerald-50 text-emerald-800 rounded-xl border border-emerald-200 text-xs font-bold shadow-sm">
            <Award className="w-4 h-4 text-emerald-600" />
            <span>Optimal Architecture:</span>
            <span className="font-mono text-emerald-900 uppercase font-black">{performance.best_model}</span>
          </div>
        )}
      </div>

      {error ? (
        <div className="bg-amber-50/80 border border-amber-200 p-6 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
            <div>
              <h4 className="text-xs font-bold text-amber-900">Evaluation Pipeline Notice</h4>
              <p className="text-xs text-amber-800 mt-0.5">{error}</p>
            </div>
          </div>
          <button
            onClick={loadData}
            className="px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl hover:bg-amber-700 transition flex items-center gap-2 cursor-pointer w-fit"
          >
            <RotateCw className="w-3.5 h-3.5" />
            <span>Retry Metric Load</span>
          </button>
        </div>
      ) : (
        <>
          {/* Models Comparative Grid */}
          {performance && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {performance.models.map((m: any) => {
                const isBest = m.model === performance.best_model;
                return (
                  <div
                    key={m.model}
                    className={`bg-card rounded-2xl border p-5 shadow-sm transition-all duration-200 flex flex-col justify-between ${
                      isBest ? 'border-primary-500 ring-2 ring-primary-500/20 bg-primary-50/10' : 'border-border hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-5">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs ${
                          isBest ? 'bg-primary-600 text-white shadow-sm' : 'bg-muted text-foreground'
                        }`}>
                          {m.model.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-black text-base text-foreground font-mono">{m.model}</h3>
                          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
                            {isBest ? 'Production Champion' : 'Benchmark Candidate'}
                          </span>
                        </div>
                      </div>

                      {isBest && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-100 text-emerald-800 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" />
                          Top Score
                        </span>
                      )}
                    </div>

                    <div className="space-y-2.5">
                      <div className="flex justify-between items-center bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Root Mean Squared (RMSE)</span>
                        <span className="text-xs font-black font-mono text-foreground">{m.RMSE.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Mean Absolute Error (MAPE)</span>
                        <span className="text-xs font-black font-mono text-primary-600">{m.MAPE.toFixed(2)}%</span>
                      </div>
                      <div className="flex justify-between items-center bg-muted/40 p-3 rounded-xl border border-border/60">
                        <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Goodness of Fit (R²)</span>
                        <span className="text-xs font-black font-mono text-emerald-600">{m.R2.toFixed(4)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Neural Weights Explorer */}
          {weights && weights.weights && (
            <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
              <div className="p-4 bg-muted/40 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <Binary className="w-4 h-4 text-purple-600" />
                  <div>
                    <h3 className="font-bold text-xs text-foreground">Deep Neural Network Architecture & Tensor Weights</h3>
                    <p className="text-[11px] text-muted-foreground">Sample weight tensors extracted from trained network weights</p>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded bg-muted text-muted-foreground">
                  lstm_model.keras
                </span>
              </div>

              <div className="p-6 space-y-5">
                {weights.weights.map((layer: any, idx: number) => (
                  <div key={idx} className="bg-muted/30 rounded-xl p-4 border border-border/80">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2.5">
                        <span className="w-6 h-6 rounded-lg bg-primary-600 text-white flex items-center justify-center text-[11px] font-bold font-mono">
                          {idx + 1}
                        </span>
                        <div>
                          <p className="text-xs font-bold text-foreground font-mono">{layer.name}</p>
                          <p className="text-[10px] text-muted-foreground font-mono">
                            Tensors: {layer.shapes.map((s: any) => `[${s.join('×')}]`).join(' • ')}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
                      {layer.sample.map((s: number[], sIdx: number) => (
                        <div key={sIdx} className="bg-card p-2.5 rounded-lg border border-border shadow-xs">
                          <p className="text-[10px] font-bold text-primary-600 uppercase mb-1.5 font-mono">
                            Kernel Head {sIdx + 1}
                          </p>
                          <div className="flex flex-wrap gap-1">
                            {s.map((w, wIdx) => (
                              <span key={wIdx} className="px-1.5 py-0.5 bg-muted text-[10px] font-mono text-muted-foreground rounded font-semibold">
                                {w.toFixed(4)}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PerformanceIndicators;
